import React from "react";
import * as d3 from "d3";

export default function ArithmeticSeriesFlipDemo({
  a1 = 3,
  d = 2,
  n = 5,
  width = 500,
  height = 300
}) {
  // 25% margin at top and bottom
  const topMargin = 0.25 * height;
  const bottomMargin = 0.25 * height;
  // typical left/right margin
  const leftMargin = 40;
  const rightMargin = 30;

  // the space left for bars
  const innerWidth = width - leftMargin - rightMargin;
  const innerHeight = height - topMargin - bottomMargin;

  // build forward + backward arrays
  const forward = [];
  for (let i = 0; i < n; i++) {
    forward.push(a1 + i * d);
  }
  const backward = [...forward].reverse();

  const maxVal = Math.max(...forward, ...backward);
  // We’ll split innerHeight in half for top row vs. bottom row
  const halfHeight = innerHeight / 2;
  const yScale = d3.scaleLinear().domain([0, maxVal]).range([0, halfHeight]);

  const gap = 8;
  const barWidth = Math.min(45, (innerWidth - gap * (n - 1)) / n);

  const shapes = [];
  forward.forEach((valTop, i) => {
    const valBot = backward[i];
    const xPos = i * (barWidth + gap);

    // top bar
    shapes.push({
      x: xPos,
      y: 0 + (halfHeight - yScale(valTop)),
      width: barWidth,
      height: yScale(valTop),
      color: "#1976d2",
      label: `a${i+1} = ${valTop}`,
      topBar: true
    });

    // bottom bar
    shapes.push({
      x: xPos,
      y: halfHeight,
      width: barWidth,
      height: yScale(valBot),
      color: "#d32f2f",
      label: `a${n - i} = ${valBot}`,
      topBar: false
    });
  });

  return (
    <svg
      width={width}
      height={height}
      style={{ background: "#fafafa", border: "1px solid #ccc" }}
    >
      {/* Place a title about 15% from the top, so a bit below the 25% margin */}
      <text
        x={width / 2}
        y={0.12 * height}
        textAnchor="middle"
        fontSize={16}
        fontWeight="bold"
      >
        Arithmetic Series “Flip” Demonstration
      </text>

      {/* Shift bars right by leftMargin, down by topMargin */}
      <g transform={`translate(${leftMargin}, ${topMargin})`}>
        {/* Bars */}
        {shapes.map((s, idx) => (
          <rect
            key={idx}
            x={s.x}
            y={s.y}
            width={s.width}
            height={s.height}
            fill={s.color}
          />
        ))}

        {/* Labels above top bars, below bottom bars */}
        {shapes.map((s, idx) => {
          const fontSize = 14;
          const labelX = s.x + s.width / 2;
          let labelY;
          if (s.topBar) {
            labelY = s.y - 6; // above the top bar
          } else {
            labelY = s.y + s.height + (fontSize + 2); // below bottom bar
          }
          return (
            <text
              key={"lbl-" + idx}
              x={labelX}
              y={labelY}
              textAnchor="middle"
              fontSize={fontSize}
              fill="#000"
            >
              {s.label}
            </text>
          );
        })}
      </g>



      {/* Legend near bottom margin */}
      <text
        x={leftMargin}
        y={height - bottomMargin + 40}
        fontSize={14}
        fill="#1976d2"
      >
        Top row: Forward sequence (blue)
      </text>
      <text
        x={leftMargin}
        y={height - bottomMargin / 2 + 25}
        fontSize={14}
        fill="#d32f2f"
      >
        Bottom row: Flipped sequence (red)
      </text>
    </svg>
  );
}
